import axios from "axios";
import { QRCodeCanvas } from "qrcode.react";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { appName, FONTSIZE, imagesUrls } from "../../../constants";
import CommonButton from "../../components/CommonButton";
import CommonNavBar from "../../components/CommonNavBar";
import CommonPageTitle from "../../components/CommonPageTitle";
import PageView from "../../components/PageView";
import useThemeHook from "../../hooks/useThemeHook";
import { useTranslationHook } from "../../hooks/useTranslationHook";
import { THEME_COLORS } from "../../themes/DarkLight";

interface Crypto {
  image: string;
  price_change_percentage_24h: number;
  current_price: number;
  name: string;
}

const initialCryptos: Crypto[] = [
  {
    image: imagesUrls["arthereum"],
    price_change_percentage_24h: 0,
    current_price: 0.001,
    name: "Arthereum",
  },
  {
    image: imagesUrls["pi"],
    price_change_percentage_24h: 0,
    current_price: 1,
    name: "PiCoin",
  },
];

const ReceiveCryptoPage: React.FC = () => {
  const getTranslationText = useTranslationHook();
  const userData = useSelector((state: any) => state?.actualUser);
  const location = useLocation();
  const user = userData?.user;
  const [cryptos, setCryptos] = useState<Crypto[]>(initialCryptos);
  const [selectedCrypto, setSelectedCrypto] = useState<Crypto | null>(null);
  const [amount, setAmount] = useState<number>(0);
  const [qrData, setQrData] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const theme = useThemeHook();

  useEffect(() => {
    const fetchCryptoPrices = async () => {
      try {
        const response = await axios.get(
          "https://api.coingecko.com/api/v3/coins/markets",
          {
            params: {
              vs_currency: "usd",
              ids: "bitcoin,ethereum,tether",
            },
          }
        );

        // const fetchedCryptos = response.data.map((crypto: any) => ({
        //   image: crypto.image,
        //   price_change_percentage_24h: crypto.price_change_percentage_24h,
        //   current_price: crypto.current_price,
        //   name: crypto.name,
        // }));
        const fetchedCryptos: any[] = [];
        const stateCryptoName = location.state?.crypto?.name;
        const initialInputCrypto =
          fetchedCryptos.find(
            (crypto: any) => crypto.name === stateCryptoName
          ) ||
          initialCryptos.find(
            (crypto: any) => crypto.name === stateCryptoName
          ) ||
          fetchedCryptos[0];
        setCryptos([...initialCryptos, ...fetchedCryptos]);
        setSelectedCrypto(initialInputCrypto);
      } catch (error) {
        console.error("Error fetching crypto prices:", error);
      }
    };

    fetchCryptoPrices();
  }, []);

  const handleCryptoChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selected = cryptos.find((c) => c.name === e.target.value);
    setSelectedCrypto(selected || null);
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value) || 0;
    setAmount(value);
  };

  const handleGenerateQR = () => {
    if (selectedCrypto?.name?.trim()?.toLowerCase() !== "picoin") {
      alert(getTranslationText("only_pi"));
      return;
    }
    if (!user || !user.username) {
      setError("Username not available.");
      return;
    }

    if (!selectedCrypto) {
      setError("Please select a crypto.");
      return;
    }

    if (amount <= 0) {
      setError("Amount must be greater than 0.");
      return;
    }

    const data = {
      username: user.username,
      crypto: selectedCrypto.name,
      amount: amount.toFixed(6),
    };
    setQrData(JSON.stringify(data));
    setError(null);
  };

  return (
    <PageView>
      <CommonNavBar />
      <CommonPageTitle title={getTranslationText("receive")} padding={null} />
      <div
        style={{
          width: "100%",
          padding: "10px",
          backgroundColor: THEME_COLORS[theme].component_background_color,
          borderRadius: "10px",
          boxShadow: "0 5px 15px rgba(0,0,0,0.1)",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "20px",
          }}
        >
          <p
            style={{
              fontSize: FONTSIZE.QUATERNARY,
              color: THEME_COLORS[theme].inverse_theme,
            }}
          >
            {getTranslationText("username")}: {user?.username || "N/A"}
          </p>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
          >
            <label
              style={{
                display: "block",
                marginBottom: "5px",
                fontSize: FONTSIZE.QUATERNARY,
                color: THEME_COLORS[theme].inverse_theme,
              }}
            >
              {getTranslationText("crypto")}
            </label>
            <img
              src={selectedCrypto?.image || ""}
              alt={selectedCrypto?.name || "Select Crypto"}
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                backgroundColor: THEME_COLORS[theme].less_theme,
              }}
            />
            <select
              value={selectedCrypto?.name || ""}
              onChange={handleCryptoChange}
              style={{
                padding: "10px",
                backgroundColor: THEME_COLORS[theme].component_background_color,
                color: THEME_COLORS[theme].inverse_theme,
                border: "1px solid #ccc",
                borderRadius: "5px",
              }}
            >
              <option value="">{getTranslationText("crypto")}</option>
              {cryptos.map((crypto) => (
                <option key={crypto.name} value={crypto.name}>
                  {crypto.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "5px",
                fontSize: FONTSIZE.QUATERNARY,
                color: THEME_COLORS[theme].inverse_theme,
              }}
            >
              {getTranslationText("amount")}
            </label>
            <input
              type="number"
              value={amount}
              onChange={handleAmountChange}
              style={{
                width: "100%",
                padding: "10px",
                backgroundColor: THEME_COLORS[theme].less_theme,
                color: THEME_COLORS[theme].inverse_theme,
                border: "1px solid #ccc",
                borderRadius: "5px",
              }}
            />
          </div>
          <CommonButton
            onClickHandle={handleGenerateQR}
            text={getTranslationText("receive")}
          />

          {qrData && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <p
                style={{
                  textAlign: "center",
                  marginTop: "20px",
                  color: THEME_COLORS[theme].inverse_theme,
                }}
              >
                {getTranslationText("scan_qr_code", { appName })}
              </p>

              <QRCodeCanvas style={{}} value={qrData} size={200} />
            </div>
          )}
          {error && <p style={{ color: "red" }}>{error}</p>}
          <div style={{ height: "100px" }}></div>
        </div>
      </div>
    </PageView>
  );
};

export default ReceiveCryptoPage;
